import { useEffect, useRef, useState } from 'react';
import { usePaymentApi } from '@/lib/payment-api';
import { toSubunits } from '@/lib/currency';
import type { DebtData } from '@/types';

export interface PreviewSegment {
  caseId: string;
  creditor: string;
  amount: number;
  percentage: number;
}

interface UsePaymentDistributionPreviewArgs {
  accountId: string | null;
  amount: number;
  currency: string;
  debts: DebtData[];
  /** Fetch only while the distribution accordion is open. */
  enabled: boolean;
  debounceMs?: number;
}

interface UsePaymentDistributionPreviewResult {
  segments: PreviewSegment[];
  total: number;
  isLoading: boolean;
  error: boolean;
}

interface RawPreview {
  distribution: { case_id: string; amount_subunits: number }[];
  total_subunits: number;
}

/**
 * Fetch the ledger-accurate per-case split for an account payment.
 *
 * The backend allocator is the single source of truth for the split, so this
 * hook never re-derives it. It fetches only while ``enabled`` (the distribution
 * accordion is open) and refetches, debounced, when the amount changes while
 * open.
 *
 * The fetch effect depends only on stable primitives — ``debts`` and the API
 * function are read via refs / mapped at render, so an unstable ``debts`` array
 * or API-function reference cannot re-trigger the effect (which would otherwise
 * bump the request sequence and discard every completed response, leaving it
 * stuck loading).
 */
export function usePaymentDistributionPreview({
  accountId,
  amount,
  currency,
  debts,
  enabled,
  debounceMs = 300,
}: UsePaymentDistributionPreviewArgs): UsePaymentDistributionPreviewResult {
  const { previewDistribution } = usePaymentApi();
  const previewRef = useRef(previewDistribution);
  previewRef.current = previewDistribution;

  const [raw, setRaw] = useState<RawPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!enabled || !accountId || amount <= 0) {
      setRaw(null);
      setError(false);
      setIsLoading(false);
      return;
    }

    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(false);

    const timer = setTimeout(async () => {
      try {
        const result = await previewRef.current({
          accountId,
          amountSubunits: toSubunits(amount),
          currency,
        });
        if (seq !== requestSeq.current) return;
        setRaw(result);
      } catch {
        if (seq !== requestSeq.current) return;
        setRaw(null);
        setError(true);
      } finally {
        if (seq === requestSeq.current) setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [enabled, accountId, amount, currency, debounceMs]);

  // Map case_id → creditor and compute percentages at render, so a changing
  // `debts` reference never re-triggers the fetch above.
  const totalSubunits = raw?.total_subunits || 1;
  const segments: PreviewSegment[] = raw
    ? raw.distribution.map((entry) => ({
        caseId: entry.case_id,
        creditor: debts.find((d) => d.id === entry.case_id)?.originalCreditor ?? entry.case_id,
        amount: entry.amount_subunits / 100,
        percentage: (entry.amount_subunits / totalSubunits) * 100,
      }))
    : [];

  return { segments, total: raw ? raw.total_subunits / 100 : 0, isLoading, error };
}
